import { useCallback, useSyncExternalStore } from 'react';
export const UI_THEME_SWITCH_ENABLED = false;
const STORAGE_KEY = 'kgolf-ui-theme';
const UI_THEME_EVENT = 'kgolf-ui-theme-change';
const DEFAULT_UI_THEME = 'classic';
const listeners = new Set();
let currentUiTheme = DEFAULT_UI_THEME;
let browserListenersAttached = false;
let storageInitialized = false;
function isUiTheme(value) {
    return value === 'classic' || value === 'mission-control';
}
function normalizeUiTheme(theme) {
    return UI_THEME_SWITCH_ENABLED ? theme : DEFAULT_UI_THEME;
}
export function getStoredUiTheme() {
    if (!UI_THEME_SWITCH_ENABLED)
        return DEFAULT_UI_THEME;
    if (typeof window === 'undefined')
        return currentUiTheme;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isUiTheme(stored) ? stored : DEFAULT_UI_THEME;
}
function applyUiTheme(theme) {
    if (typeof document === 'undefined')
        return;
    document.documentElement.dataset.uiTheme = theme;
}
export function initializeUiTheme() {
    currentUiTheme = normalizeUiTheme(getStoredUiTheme());
    storageInitialized = true;
    applyUiTheme(currentUiTheme);
}
function notifyUiThemeListeners() {
    listeners.forEach((listener) => listener());
}
function syncUiTheme(theme) {
    const nextTheme = normalizeUiTheme(theme);
    currentUiTheme = nextTheme;
    applyUiTheme(nextTheme);
    notifyUiThemeListeners();
}
function attachBrowserListeners() {
    if (browserListenersAttached || typeof window === 'undefined')
        return;
    window.addEventListener('storage', (event) => {
        if (event.key !== STORAGE_KEY)
            return;
        syncUiTheme(isUiTheme(event.newValue) ? event.newValue : DEFAULT_UI_THEME);
    });
    browserListenersAttached = true;
}
function subscribeToUiTheme(listener) {
    attachBrowserListeners();
    listeners.add(listener);
    if (!storageInitialized) {
        const previousTheme = currentUiTheme;
        initializeUiTheme();
        if (previousTheme !== currentUiTheme)
            notifyUiThemeListeners();
    }
    return () => {
        listeners.delete(listener);
    };
}
export function setStoredUiTheme(theme) {
    const nextTheme = normalizeUiTheme(theme);
    currentUiTheme = nextTheme;
    applyUiTheme(nextTheme);
    if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, nextTheme);
        window.dispatchEvent(new CustomEvent(UI_THEME_EVENT, { detail: nextTheme }));
    }
    notifyUiThemeListeners();
}
function getUiThemeSnapshot() {
    return currentUiTheme;
}
export function useUiTheme() {
    const uiTheme = useSyncExternalStore(subscribeToUiTheme, getUiThemeSnapshot, getUiThemeSnapshot);
    const setUiTheme = useCallback((theme) => {
        setStoredUiTheme(theme);
    }, []);
    const toggleUiTheme = useCallback(() => {
        if (!UI_THEME_SWITCH_ENABLED) {
            setStoredUiTheme(DEFAULT_UI_THEME);
            return;
        }
        setStoredUiTheme(currentUiTheme === 'mission-control' ? 'classic' : 'mission-control');
    }, []);
    return {
        uiTheme,
        canToggleUiTheme: UI_THEME_SWITCH_ENABLED,
        isMissionControlTheme: uiTheme === 'mission-control',
        setUiTheme,
        toggleUiTheme,
    };
}
